import Image from 'next/image';

import {
  Box,
  Flex,
  Heading,
  Skeleton,
  Table,
  TableCaption,
  TableCellProps,
  TableColumnHeaderProps,
  TableContainer,
  TableRowProps,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VisuallyHidden,
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@udacity/chakra-uds-icons';

import { ContentContainer } from '~/components/container';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { usePricePlans } from '~/features/plans/hooks/use-price-plans';

export type FeatureComparisonTableSectionProps = {
  title: string;
};

const columnHeaderSharedProps: Partial<TableColumnHeaderProps> = {
  scope: 'column',
  whiteSpace: 'normal',
  textAlign: 'center',
  borderRadius: 'lg',
  borderRight: 'none !important',
  letterSpacing: 'normal',
};

const rowHeaderSharedProps: Partial<TableColumnHeaderProps> = {
  whiteSpace: 'normal',
  scope: 'row',
  backgroundColor: 'none',
  fontFamily: 'body',
  letterSpacing: 'normal',
  fontWeight: {
    base: '400',
    md: '600',
  },
  borderRight: 'none !important',
  borderBottom: '1px solid',
  padding: '0px 8px 0px 0px',
  fontSize: 'sm',
};

const tableCellSharedProps: Partial<TableCellProps> = {
  backgroundColor: 'none',
  borderRight: 'none',
  position: 'relative',
  textAlign: 'center',
  whiteSpace: 'pre-wrap',
  sx: {
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      borderRight: '10px solid red',
      left: 0,
      top: 0,
      bottom: 0,
    },
  },
  fontWeight: '600',
  fontSize: {
    base: 'sm',
    md: 'md',
  },
};

const tableCellUdacityColumnSharedProps: Partial<TableCellProps> = {
  bgColor: '#95A4FC33',
  sx: {
    '&::after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      borderRight: {
        base: 'unset',
        md: '18px solid white',
      },
      right: 0,
      top: 0,
      bottom: 0,
    },
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      borderRight: {
        base: 'unset',
        md: '10px solid white',
      },
      left: 0,
      top: 0,
      bottom: 0,
    },
  },
};
const tableCellUniversitiesColumnSharedProps: Partial<TableCellProps> = {
  bgColor: '#6197FF14',
  sx: {
    '&::after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      borderRight: {
        base: 'unset',
        md: '18px solid white',
      },
      right: 0,
      top: 0,
      bottom: 0,
    },
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      borderRight: {
        base: 'unset',
        md: '10px solid white',
      },
      left: 0,
      top: 0,
      bottom: 0,
    },
  },
};
const tableCellBootcampsColumnSharedProps: Partial<TableCellProps> = {
  background: '#9EF5E426',
  sx: {
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
    },
    '&::after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
    },
    '&::before,&::after': {
      borderRight: {
        base: 'unset',
        md: '10px solid white',
      },
    },
  },
};

const tableRowSharedProps: Partial<TableRowProps> = {
  height: '72px',
};

function ShapesBackground() {
  return (
    <Box height='690px' position='relative' width='604px'>
      <Image
        alt=''
        src='/images/backgrounds/floating-shapes-2.png'
        style={{
          objectFit: 'cover',
          pointerEvents: 'none',
        }}
        fill
      />
    </Box>
  );
}

export function FeatureComparisonTableSection({ title }: FeatureComparisonTableSectionProps) {
  return (
    <Box
      as='section'
      bgColor='white'
      overflow='hidden'
      padding={{
        base: '40px 0px',
        xl: '80px 0px',
      }}
    >
      <Box
        margin='0 auto'
        maxWidth='content-max-width'
        position='relative'
        display={{
          base: 'none',
          xl: 'block',
        }}
      >
        <Box left={-540} position='absolute' top={0} transform='rotate(-162.34deg) scaleY(-1)'>
          <ShapesBackground />
        </Box>
        <Box position='absolute' right={-540} top={0} transform='rotate(172.1deg)'>
          <ShapesBackground />
        </Box>
      </Box>
      <ContentContainer position='relative'>
        <Heading
          as='h2'
          mb={12}
          textAlign='center'
          size={{
            base: 'marketing-3xl',
            md: 'marketing-4xl',
          }}
          whiteSpace={{
            xl: 'pre-wrap',
          }}
        >
          {title}
        </Heading>
        <Flex
          marginLeft={{
            base: '-16px',
            md: 'unset',
          }}
          marginRight={{
            base: '-16px',
            md: 'unset',
          }}
        >
          <TableContainer
            margin='0 auto'
            maxWidth='922px'
            overflowX={{ base: 'auto', md: 'visible' }}
            overflowY={{ base: 'hidden', md: 'visible' }}
            width='100%'
          >
            <Table
              bgColor='white'
              border='none'
              variant='simple'
              paddingEnd={{
                base: '16px',
                md: 'unset',
              }}
              paddingStart={{
                base: '16px',
                md: 'unset',
              }}
              sx={{
                borderCollapse: 'separate',
                borderSpacing: '0px',
              }}
            >
              <VisuallyHidden as={TableCaption}>
                Feature comparisons between Udacity, Universities, and Technical Bootcamps.
              </VisuallyHidden>
              <colgroup>
                <col style={{ width: '16%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <Thead>
                <Tr>
                  <VisuallyHidden as={Th} scope='column'>
                    Features
                  </VisuallyHidden>
                  <Th
                    {...columnHeaderSharedProps}
                    bgColor='blue.700'
                    position='relative'
                    zIndex={1}
                    transform={{
                      md: 'scale(1.1)',
                    }}
                  >
                    <Box
                      display={{
                        base: 'none',
                        md: 'block',
                      }}
                    >
                      <Image
                        alt=''
                        height={43}
                        src='/images/logos/udacity-accenture.svg'
                        width={185}
                        style={{
                          display: 'inline-block',
                          objectFit: 'contain',
                          height: '38px',
                        }}
                      />
                    </Box>
                    <Box
                      display={{
                        base: 'block',
                        md: 'none',
                      }}
                    >
                      <Image
                        alt=''
                        height={34.85}
                        src='/images/logos/udacity-icon.svg'
                        width={34.85}
                        style={{
                          display: 'inline-block',
                          height: 'auto',
                          maxWidth: '100%',
                        }}
                      />
                    </Box>
                    <VisuallyHidden>Udacity - Part of Accenture</VisuallyHidden>
                  </Th>
                  <Heading
                    as={Th}
                    {...columnHeaderSharedProps}
                    bgColor='#AEC6FF'
                    fontWeight='400'
                    size={{
                      md: 'marketing-lg',
                    }}
                    transform={{
                      md: 'translateX(-8px)',
                    }}
                  >
                    Universities
                  </Heading>
                  <Heading
                    as={Th}
                    {...columnHeaderSharedProps}
                    bgColor='#99E8D9'
                    fontWeight='400'
                    size={{
                      md: 'marketing-lg',
                    }}
                  >
                    Technical Bootcamps
                  </Heading>
                </Tr>
              </Thead>
              <Tbody>
                <Tr {...tableRowSharedProps}>
                  <Th {...rowHeaderSharedProps}>Completion Rates</Th>
                  <Td {...tableCellSharedProps} {...tableCellUdacityColumnSharedProps}>
                    <VisuallyHidden>High</VisuallyHidden>
                    <Meter value='high' />
                  </Td>
                  <Td {...tableCellSharedProps} {...tableCellUniversitiesColumnSharedProps}>
                    <VisuallyHidden>Moderate</VisuallyHidden>
                    <Meter value='medium' />
                  </Td>
                  <Td {...tableCellSharedProps} {...tableCellBootcampsColumnSharedProps}>
                    <VisuallyHidden>High</VisuallyHidden>
                    <Meter value='high' />
                  </Td>
                </Tr>
                <Tr {...tableRowSharedProps}>
                  <Th {...rowHeaderSharedProps}>Flexibility for learner</Th>
                  <Td {...tableCellSharedProps} {...tableCellUdacityColumnSharedProps}>
                    <VisuallyHidden>High</VisuallyHidden>
                    <Meter value='high' />
                  </Td>
                  <Td {...tableCellSharedProps} {...tableCellUniversitiesColumnSharedProps}>
                    <VisuallyHidden>Low</VisuallyHidden>
                    <Meter value='low' />
                  </Td>
                  <Td {...tableCellSharedProps} {...tableCellBootcampsColumnSharedProps}>
                    <VisuallyHidden>Low</VisuallyHidden>
                    <Meter value='low' />
                  </Td>
                </Tr>
                <Tr {...tableRowSharedProps}>
                  <Th {...rowHeaderSharedProps}>Skill level</Th>
                  <Td {...tableCellSharedProps} {...tableCellUdacityColumnSharedProps}>
                    Practitioner
                  </Td>
                  <Td {...tableCellSharedProps} {...tableCellUniversitiesColumnSharedProps}>
                    {'Practitioner &\nTheoretical'}
                  </Td>
                  <Td {...tableCellSharedProps} {...tableCellBootcampsColumnSharedProps}>
                    Practitioner
                  </Td>
                </Tr>
                <Tr {...tableRowSharedProps}>
                  <Th {...rowHeaderSharedProps}>Cost</Th>
                  <UdacityMonthlyCostTd />
                  <Td {...tableCellSharedProps} {...tableCellUniversitiesColumnSharedProps}>
                    $60K+<sup>*</sup>
                  </Td>
                  <Td {...tableCellSharedProps} {...tableCellBootcampsColumnSharedProps}>
                    $13K+{' '}
                    <Text as='span' color='gray.700' display='inline-block'>
                      (avg)
                      <Box as='sup' color='black'>
                        **
                      </Box>
                    </Text>
                  </Td>
                </Tr>
                <Tr {...tableRowSharedProps}>
                  <Th {...rowHeaderSharedProps}>Unlimited course access</Th>
                  <Td {...tableCellSharedProps} {...tableCellUdacityColumnSharedProps}>
                    <CheckIcon color='accent.sea-foam.700' height='32px' width='32px' />
                    <VisuallyHidden>Yes</VisuallyHidden>
                  </Td>
                  <Td {...tableCellSharedProps} {...tableCellUniversitiesColumnSharedProps}>
                    <CloseIcon color='red.500' height='32px' width='32px' />
                    <VisuallyHidden>No</VisuallyHidden>
                  </Td>
                  <Td {...tableCellSharedProps} {...tableCellBootcampsColumnSharedProps}>
                    <CloseIcon color='red.500' height='32px' width='32px' />
                    <VisuallyHidden>No</VisuallyHidden>
                  </Td>
                </Tr>
                <Tr {...tableRowSharedProps}>
                  <Th {...rowHeaderSharedProps}>
                    Total time
                    <Text as='span' display='inline-block' fontSize='xs' fontWeight='400'>
                      (Technical education)
                    </Text>
                  </Th>
                  <Td {...tableCellSharedProps} {...tableCellUdacityColumnSharedProps}>
                    4 Months{' '}
                    <Text as='span' color='gray.700' display='inline-block'>
                      (avg)
                    </Text>
                  </Td>
                  <Td {...tableCellSharedProps} {...tableCellUniversitiesColumnSharedProps}>
                    4 years
                  </Td>
                  <Td {...tableCellSharedProps} {...tableCellBootcampsColumnSharedProps}>
                    6 months{' '}
                    <Text as='span' color='gray.700' display='inline-block'>
                      (avg)
                    </Text>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>
        </Flex>
      </ContentContainer>
    </Box>
  );
}

function Meter({ value }: { value?: 'low' | 'medium' | 'high' }) {
  return (
    <Flex
      display='inline-flex'
      transform={{
        base: 'scale(0.7)',
        md: 'scale(1)',
      }}
    >
      {value === 'low' && <Image alt='' height={12} src='/images/career-outcomes/meter-red.svg' width={68} />}
      {value === 'medium' && <Image alt='' height={12} src='/images/career-outcomes/meter-yellow.svg' width={68} />}
      {value === 'high' && <Image alt='' height={12} src='/images/career-outcomes/meter-green.svg' width={68} />}
    </Flex>
  );
}

function UdacityMonthlyCostTd() {
  const { currency } = useCurrency();
  const { data, isLoading } = usePricePlans(currency);
  const monthlyCost = data?.prices?.monthly?.individual?.displayPrice;
  return (
    <Td {...tableCellSharedProps} {...tableCellUdacityColumnSharedProps}>
      {(isLoading || !monthlyCost) && <Skeleton display='inline-block' height='26px' width='100px' />}
      {!isLoading && Boolean(monthlyCost) && (
        <>
          {monthlyCost}{' '}
          <Box as='span' color='gray.700'>
            per month
          </Box>
        </>
      )}
    </Td>
  );
}
